import { ClassValue } from 'clsx';
import { CSSProperties, ReactNode } from 'react';
import CheckmarkFilledIcon from '@shared/assets/icons/checkmark/filled.svg';
import CrossFilledIcon from '@shared/assets/icons/cross/filled.svg';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { MBImage } from '@shared/components/image';
import { LoadingIndicator } from '@shared/components/loading';
import { Colors, bounceIn, fadeIn } from '@shared/styles';
import classNames from './deliveryStatus.module.css';

interface MBMessageDeliveryStatusProps {
  status: MBDeliveryStatus | null;
  isEdited?: boolean;
  style?: CSSProperties;
  testID?: string;
}

interface MBMessageDeliveryStatusContentProps {
  text?: string;
  icon: ReactNode;
  className?: ClassValue;
  style?: CSSProperties;
  testID?: string;
}

/*
    Component to show a message's delivery status. Possible statuses:
      * sending or uploadingAttachments (spinning almost closed blue circle)
      * failed (red X with "Failed" text)
      * waiting (semitransparent circle with "Waiting" text)
      * sent (blue checkmark with "Sent" text)
*/
export function MBMessageDeliveryStatus({
  status,
  isEdited,
  style,
  testID,
}: MBMessageDeliveryStatusProps) {
  if (status === 'sending' || status === 'uploadingAttachments') {
    return (
      <MBMessageDeliveryStatusContent
        style={style}
        icon={<LoadingIndicator size="small" className={classNames.spinner} />}
      />
    );
  }

  if (status === 'failed') {
    return (
      <MBMessageDeliveryStatusContent
        style={style}
        text="Failed to send"
        icon={
          <View fill className={bounceIn}>
            <MBImage fill tintColor={Colors.v2.red} source={CrossFilledIcon} />
          </View>
        }
      />
    );
  }

  if (status === 'waiting') {
    return (
      <MBMessageDeliveryStatusContent
        className={classNames.waitingToSend}
        style={style}
        text="Waiting to send"
        icon={
          <View fill className={bounceIn}>
            <LoadingIndicator size="small" className={classNames.spinner} />
          </View>
        }
      />
    );
  }

  if (status === 'sent') {
    const text = `${isEdited ? '(edited) • ' : ''}Delivered`;

    return (
      <MBMessageDeliveryStatusContent
        style={style}
        text={text}
        icon={
          <View fill className={bounceIn}>
            <MBImage fill tintColor={Colors.v2.green} source={CheckmarkFilledIcon} />
          </View>
        }
        testID={testID}
      />
    );
  }

  return null;
}

function MBMessageDeliveryStatusContent({
  text,
  icon,
  className,
  style,
  testID,
}: MBMessageDeliveryStatusContentProps) {
  return (
    <View className={[classNames.messageDeliveryStatus, className]} style={style} testID={testID}>
      {!!text && (
        <View className={fadeIn}>
          <Text size="caption2">{text}</Text>
        </View>
      )}
      <View className={classNames.icon}>{icon}</View>
    </View>
  );
}
